import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserData } from '../auth/UserData';
import { useGoogleOAuth } from '../hooks/useGoogleOAuth';
import SEO from '../components/SEO';
import AuthLayout from './landing/AuthLayout';
import { GoogleIcon, DiscordIcon } from './landing/icons';

const Login = () => {
  const { t } = useTranslation();
  const { user, isLoading, refreshUserData, setUser } = useUserData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formValues, setFormValues] = useState({ email: '', password: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const backendUrl = window.__BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Show error from Discord redirect (e.g. /login?error=discord)
  useEffect(() => {
    if (searchParams.get('error') === 'discord') {
      setErrorMessage(t('login.errors.oauth'));
    }
  }, [searchParams, t]);

  // Redirect to /app when user is authenticated
  useEffect(() => {
    if (!isLoading && user) {
      navigate('/app', { replace: true });
    }
  }, [isLoading, user, navigate]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailLogin = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!formValues.email || !formValues.password) {
      setErrorMessage(t('login.errors.missingFields'));
      return;
    }

    if (!backendUrl) {
      setErrorMessage(t('common.backendUnavailable'));
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${backendUrl}/auth/login`,
        { email: formValues.email, password: formValues.password },
        { withCredentials: true }
      );
      if (response.data?.user) {
        setUser(response.data.user);
      } else {
        await refreshUserData();
      }
      navigate('/app', { replace: true });
    } catch (error) {
      if (error.response?.status === 401) {
        setErrorMessage(t('login.errors.invalidCredentials'));
      } else if (error.response?.status === 400) {
        setErrorMessage(t('login.errors.missingFields'));
      } else {
        setErrorMessage(t('login.errors.server'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Discord login — full-page redirect (no popup).
  // The backend callback redirects back to /app after authentication.
  const handleDiscordLogin = () => {
    if (!backendUrl) {
      setErrorMessage(t('common.backendUnavailable'));
      return;
    }
    window.location.href = `${backendUrl}/auth/discord`;
  };

  const handleGoogleSuccess = async (code) => {
    try {
      setSubmitting(true);
      setErrorMessage('');

      if (!backendUrl) {
        setErrorMessage(t('common.backendUnavailable'));
        return;
      }

      const response = await axios.post(`${backendUrl}/auth/google`, { code }, { withCredentials: true });
      if (response.data?.user) {
        setUser(response.data.user);
      } else {
        await refreshUserData();
      }
      navigate('/app', { replace: true });
    } catch (error) {
      if (error.response?.status === 503 || error.response?.status === 502) {
        setErrorMessage(t('login.errors.googleUnavailable'));
      } else {
        setErrorMessage(t('login.errors.oauth'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const { requestCode: requestGoogleCode } = useGoogleOAuth({
    onSuccess: handleGoogleSuccess,
    onError: (err) => console.warn('Google OAuth:', err.message),
  });

  const handleGoogleLogin = () => {
    setErrorMessage('');
    if (!googleClientId) {
      setErrorMessage(t('login.errors.googleUnavailable'));
      return;
    }
    requestGoogleCode();
  };

  if (isLoading) {
    return (
      <div className="ftv2">
        <div className="auth-loading"><span className="spinner" style={{ color: 'var(--orange)' }} /></div>
      </div>
    );
  }

  return (
    <AuthLayout>
      <SEO title="Log In" path="/login" noindex />

      <h1 className="auth-title">{t('login.title')}</h1>
      <p className="auth-sub">{t('login.description')}</p>

      {searchParams.get('reset') === 'success' && (
        <div className="auth-alert success" role="status">
          {t('terminal.login.resetSuccessBanner', 'Password reset successfully. Please sign in with your new password.')}
        </div>
      )}
      {errorMessage && (
        <div className="auth-alert error" role="alert" id="login-error">{errorMessage}</div>
      )}

      <form className="auth-form" onSubmit={handleEmailLogin} noValidate aria-label="Logg inn">
        <div className="field">
          <label htmlFor="login-email">{t('login.emailLabel')}</label>
          <input
            id="login-email"
            name="email"
            type="email"
            value={formValues.email}
            onChange={handleInputChange}
            required
            autoComplete="email"
            aria-describedby={errorMessage ? 'login-error' : undefined}
          />
        </div>
        <div className="field">
          <label htmlFor="login-password">{t('login.passwordLabel')}</label>
          <input
            id="login-password"
            name="password"
            type="password"
            value={formValues.password}
            onChange={handleInputChange}
            required
            autoComplete="current-password"
          />
        </div>
        <div className="auth-forgot">
          <RouterLink to="/forgot-password">{t('login.forgotPassword', 'Forgot password?')}</RouterLink>
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting} aria-busy={submitting}>
          {submitting ? <span className="spinner" aria-label="Logger inn..." /> : t('login.submit')}
        </button>
      </form>

      <div className="auth-divider">{t('login.orDivider')}</div>

      <div className="auth-oauth">
        <button type="button" className="btn btn-discord" onClick={handleDiscordLogin}>
          <DiscordIcon />{t('login.discordButton')}
        </button>
        <button type="button" className="btn btn-google" onClick={handleGoogleLogin}>
          <GoogleIcon />{t('login.googleButton')}
        </button>
      </div>

      <p className="auth-switch">
        {t('login.noAccount')}{' '}
        <RouterLink to="/signup">{t('login.registerLink')}</RouterLink>
      </p>
    </AuthLayout>
  );
};

export default Login;
